import React, { useEffect, useState } from "react";

import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  discord,
  facebook,
  hidepassword,
  meta,
  showpassword,
  xIcon,
} from "@/constants";
import { useLoginUser, useMetaMaskLogin } from "@/services/auth.service";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import useUserStore from "@/stores/authentication/store";
import useBalanceStore from "@/stores/user/balance.store";
import { authApiURL } from "@/services/api.service";
import { Input } from "@/components/ui/input";
import StatusDialog from "@/components/dialogs/StatusDialog";
import LoadingSpinner from "@/components/loaders/LoadingSpinner";
import useSelfExclusionStore from "@/stores/selfexclusion/store";
import Cookies from "js-cookie";
import {
  CreateBonusUserPayload,
  useCreateBonusProfile,
} from "@/services/bonus.service.ts";
import { BonusUserDomainEnum } from "@/pages/Bonus/components/LoginBonus/enums.tsx";
import EmailModal from "./MetaEmailDialog";

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Required")
    .email({ message: "Please enter a valid email address." }),
  password: z.string().min(1, "Required"), // Assuming password is required
});

type LoginFormInputs = z.infer<typeof loginSchema>;

interface LoginFormProps {
  setPasswordReset: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ setPasswordReset, onClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors: _errors },
    watch,
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const navigate = useNavigate();
  const { mutate } = useLoginUser();

  const email = watch("email");
  const password = watch("password");

  type Errors = {
    email?: { message: string };
    password?: { message: string };
  };

  const [loginError, setLoginError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [errors, setErrors] = useState<Errors | null>(null);

  const [isSocialLoginErrorDialogOpen, setIsSocialLoginErrorDialogOpen] =
    useState<boolean>(false);

  useEffect(() => {
    setLoginError("");
    setEmailError("");
    setPasswordError("");
    setErrors(null);
  }, [email, password]);

  useEffect(() => {
    if (_errors) setErrors(_errors as Errors);
  }, [_errors]);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const isSelfExclusion = useSelfExclusionStore(
    (state) => state.isSelfExclusion,
  );
  const setOpenPopup = useSelfExclusionStore((state) => state.setOpenPopup);

  const userStore = useUserStore();
  const balanceStore = useBalanceStore();

  const { mutate: mutateCreateBonusProfile } = useCreateBonusProfile();

  const createBonusProfile = (id: string, username: string) => {
    try {
      mutateCreateBonusProfile(
        {
          id,
          username,
          domain: BonusUserDomainEnum.SHRL,
        } as CreateBonusUserPayload,
        {
          onSuccess: () => {
            console.log("create bonus profile success");
          },
          onError: (error: unknown) => {
            console.error("create user error:", error);
          },
        },
      );
    } catch (error) {
      console.error("Unexpected error:", error);
      console.error("An unexpected error occurred. Please try again.");
    }
  };

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    try {
      mutate(data, {
        onSuccess: (res) => {
          const loginResponse = res.data.data.profile;
          userStore.fetchUserData((data: any) => {
            const id = data?.id || "";
            const username = data?.name || "";
            createBonusProfile(id, username);
          });
          userStore.setData({
            ...userStore.data,
            accountBalance: loginResponse.accountBalance,
            nativeBalance: loginResponse.nativeBalance,
          });
          userStore.setIsAuthenticated(true);
          if (isSelfExclusion) {
            setOpenPopup(true);
          }
          balanceStore.setBalance(loginResponse.accountBalance);
          balanceStore.setNativeBalance(loginResponse.nativeBalance);

          Cookies.set("jwtToken", loginResponse.token);
          onClose();
          navigate("/", { replace: true });
        },
        onError: (error: unknown) => {
          let messsage: string = "Error login";
          // @ts-expect-error Using `any` type for error handling, properties may not exist on `error`.
          if (error && error.response && error.response?.data?.error)
            // @ts-expect-error Using `any` type for error handling, properties may not exist on `error`.
            messsage = error.response.data.error;
          setLoginError(messsage);

          if (messsage.toLowerCase().includes("email")) {
            setEmailError(
              "There is no account registered with this email. Try again or register",
            );
          } else if (messsage.toLowerCase().includes("password")) {
            setPasswordError("The Password is wrong. Please try again");
          }
        },
      });
    } catch (error) {
      console.error("Unexpected error:", error);
      console.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleSocialLogin = (provider: string) => {
    const authUrl = `${authApiURL}/${provider}/auth`;
    window.location.href = authUrl;
    // window.location.href = `http://localhost:3000/api/v1/auth/${provider}/auth`;
  };

  const { mutate: loginWithMetaMask } = useMetaMaskLogin();

  const handleMetaMaskLogin = async () => {
    try {
      // @ts-expect-error Using `any` type for error handling, properties may not exist on `error`.
      if (!window.ethereum) throw new Error("No wallet provider found");
      // @ts-expect-error Using `any` type for error handling, properties may not exist on `error`.
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const message = "Please sign this message to log in to the application";
      const signature = await signer.signMessage(message);
      const address = await signer.getAddress();

      const data = {
        walletAddress: address,
        signature,
        message,
      };

      loginWithMetaMask(data, {
        onSuccess: () => {
          // handleLoginSuccess();
          // toast.success("login successful!");
        },
        onError: (error) => {
          console.error("MetaMask login failed", error);
          setIsSocialLoginErrorDialogOpen(true);
          // toast.error("MetaMask login failed");
          // let messsage: string = "Error login";
          //// @ts-expect-error Using `any` type for error handling, properties may not exist on `error`.
          // if (error && error.response && error.response?.data?.error) {
          //   // @ts-expect-error Using `any` type for error handling, properties may not exist on `error`.
          //   messsage = error.response.data.error;
          //   alert(messsage);
          // }
          // handleLoginError(error as AxiosError<LoginError>);
        },
      });
    } catch (error) {
      console.error("MetaMask login error", error);
      setIsSocialLoginErrorDialogOpen(true);
      // toast.error("MetaMask login failed");
      // handleLoginError(error as AxiosError<LoginError>);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(onSubmit)();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <div className="text-base font-semibold sm:hidden">
        Login To Your Account
      </div>
      <div className="relative mb-4 mt-3 w-full">
        <Input
          id="email"
          placeholder="Email"
          startAdornment={<img src="/assets/email.png" alt="email" />}
          className="mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 pl-9 text-white placeholder:text-white sm:text-sm"
          {...register("email")}
        />
        {errors?.email && (
          <p className="absolute right-2.5 top-1.5 mt-1.5 text-xs font-[500] text-danger">
            {errors.email.message}
          </p>
        )}
      </div>
      <div className="relative mb-3 w-full">
        <Input
          id="password"
          type={showCurrentPassword ? "text" : "password"}
          endAdornment={
            <img
              src={showCurrentPassword ? showpassword : hidepassword}
              className="mr-3 size-6 cursor-pointer"
              onClick={() => setShowCurrentPassword((prevState) => !prevState)}
            />
          }
          startAdornment={<img src="/assets/password.png" alt="password" />}
          className="mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 pl-9 text-white placeholder:text-white sm:text-sm"
          placeholder="Password"
          {...register("password")}
        />
        {errors?.password && (
          <p className="absolute right-10 top-1.5 mt-1.5 text-xs font-[500] text-danger">
            {errors.password.message}
          </p>
        )}
      </div>
      <div
        className="flex w-full cursor-pointer flex-col items-end text-[12px] text-white"
        onClick={() => setPasswordReset(true)}
      >
        Forgot Password
      </div>

      <div className="relative mb-3 mt-4 flex items-center justify-center">
        <svg
          className="absolute top-3"
          width="375"
          height="1"
          viewBox="0 0 424 1"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line opacity="0.2" y1="0.5" x2="424" y2="0.5" stroke="#69EAFF" />
        </svg>
        <p className="text-sm font-medium text-white">or login</p>
      </div>
      <div className="flex items-center justify-center gap-4">
        <Button
          type="button"
          aria-label="Login with x"
          className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
          onClick={() => handleSocialLogin("twitter")}
        >
          <img
            className="opacity-50 group-hover:opacity-100"
            src={xIcon}
            alt="xIcon"
          />
        </Button>
        <Button
          type="button"
          aria-label="Login with facebook"
          className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
          onClick={() => handleSocialLogin("facebook")}
        >
          <img
            className="opacity-50 group-hover:opacity-100"
            src={facebook}
            alt="facebook"
          />
        </Button>
        <Button
          type="button"
          aria-label="Login with discord"
          className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
          onClick={() => handleSocialLogin("discord")}
        >
          <img
            className="opacity-50 group-hover:opacity-100"
            src={discord}
            alt="discord"
          />
        </Button>
        <EmailModal
          trigger={
            <Button
              type="button"
              aria-label="Login with meta"
              className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
            >
              <img
                className="opacity-50 group-hover:opacity-100"
                src={meta}
                alt="meta"
              />
            </Button>
          }
        />
      </div>

      <Button
        onClick={handleSubmit(onSubmit)}
        className="mt-3 flex w-full rounded-md bg-button-gradient py-5 hover:bg-button-gradient-inverted"
      >
        {"Login"}
      </Button>
      {loginError && (
        <p className="mt-1.5 text-xs font-[500] text-danger">{loginError}</p>
      )}

      {/* social login error loginDialog */}
      <StatusDialog
        status="fail"
        title="Login With Social Media Failed !"
        onClose={onClose}
        message={
          <>
            There’s something wrong.
            <br />
            You cannot login with your social media.
          </>
        }
        buttonText="Try Again"
        isOpen={isSocialLoginErrorDialogOpen}
        setIsOpen={() => setIsSocialLoginErrorDialogOpen(false)}
      />
    </div>
  );
};

export default LoginForm;
